/**
 * Timeline Utility Functions
 * Date calculations, position mapping, and period generation
 */

import type { Truck, TimelinePeriod, TruckTimelineData, TimelineViewport } from './types';

/**
 * Get today's date at midnight in PST/PDT timezone (normalized)
 */
export const getTodayDate = (): Date => {
  // Get current time in PST/PDT (America/Los_Angeles)
  const pstDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  pstDate.setHours(0, 0, 0, 0);
  return pstDate;
};

/**
 * Parse a YYYY-MM-DD date string as PST/PDT (not UTC)
 * Prevents timezone shift issues when loading dates from backend
 */
export const parseDateInPST = (dateString: string): Date => {
  if (!dateString) return new Date();

  // Parse YYYY-MM-DD format
  const datePart = dateString.split('T')[0]; // Handle both "2025-10-17" and "2025-10-17T00:00:00"
  if (!datePart) return new Date();

  const parts = datePart.split('-');
  const year = parseInt(parts[0] || '0', 10);
  const month = parseInt(parts[1] || '1', 10) - 1; // Month is 0-indexed
  const day = parseInt(parts[2] || '1', 10);

  // Create date in PST timezone
  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')} 00:00:00`;
  const pstDate = new Date(new Date(dateStr).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));

  return pstDate;
};

/**
 * Snap date to midnight (floor to current day) - for magnetic day snapping
 */
export const snapToDay = (date: Date): Date => {
  const snapped = new Date(date);
  snapped.setHours(0, 0, 0, 0);
  return snapped;
};

/**
 * Add months to a date
 */
export const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

/**
 * Calculate date range for timeline viewport
 * Start: Always today
 * End: Today + monthsRange
 */
export const calculateViewport = (monthsRange: number): TimelineViewport => {
  const startDate = getTodayDate();
  const endDate = addMonths(startDate, monthsRange);

  return {
    startDate,
    endDate,
    monthsRange,
  };
};

/**
 * Convert date string to Date object (handles ISO and date input formats)
 */
export const parseDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;

  return date;
};

/**
 * Map date to X position on timeline
 */
export const dateToPosition = (
  date: Date,
  viewport: TimelineViewport,
  chartWidth: number
): number => {
  const totalMs = viewport.endDate.getTime() - viewport.startDate.getTime();
  const dateMs = date.getTime() - viewport.startDate.getTime();

  return (dateMs / totalMs) * chartWidth;
};

/**
 * Map X position to date
 */
export const positionToDate = (
  x: number,
  viewport: TimelineViewport,
  chartWidth: number
): Date => {
  const totalMs = viewport.endDate.getTime() - viewport.startDate.getTime();
  const ratio = x / chartWidth;
  const dateMs = viewport.startDate.getTime() + (ratio * totalMs);

  return new Date(dateMs);
};

/**
 * Generate time ticks for X-axis
 */
export const generateTimeTicks = (viewport: TimelineViewport): Date[] => {
  const ticks: Date[] = [];
  const { startDate, endDate } = viewport;

  // Generate monthly ticks
  const currentDate = new Date(startDate);
  currentDate.setDate(1); // Start of month

  while (currentDate <= endDate) {
    if (currentDate >= startDate) {
      ticks.push(new Date(currentDate));
    }
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return ticks;
};

/**
 * Format date for display on axis
 */
export const formatTickDate = (date: Date): string => {
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  const currentYear = new Date().getFullYear();

  // Show year if different from current
  return year !== currentYear ? `${month} ${year}` : month;
};

/**
 * Format date for tooltip display
 */
export const formatTooltipDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Check if two periods overlap
 */
const periodsOverlap = (period1: TimelinePeriod, period2: TimelinePeriod): boolean => {
  return period1.startDate <= period2.endDate && period2.startDate <= period1.endDate;
};

/**
 * Assign periods to lanes to prevent overlapping
 * Keeps segments from the same countdown bar together on same lane
 * Returns periods with assigned lane numbers (0, 1, 2, etc.)
 */
export const assignLanes = (periods: TimelinePeriod[]): Array<TimelinePeriod & { lane: number }> => {
  if (periods.length === 0) return [];

  // Group periods by groupId (countdown bar segments stay together)
  const groups = new Map<string, TimelinePeriod[]>();
  const ungrouped: TimelinePeriod[] = [];

  periods.forEach((period) => {
    if (period.groupId) {
      if (!groups.has(period.groupId)) {
        groups.set(period.groupId, []);
      }
      groups.get(period.groupId)!.push(period);
    } else {
      ungrouped.push(period);
    }
  });

  // Convert groups to array and sort by earliest start date
  const groupArray = Array.from(groups.values()).sort((a, b) => {
    const aStart = Math.min(...a.map(p => p.startDate.getTime()));
    const bStart = Math.min(...b.map(p => p.startDate.getTime()));
    return aStart - bStart;
  });

  // Track which lane each group occupies
  const lanes: TimelinePeriod[][] = [];
  const laneAssignments = new Map<string, number>();

  // Assign lanes to groups (all segments in group get same lane)
  groupArray.forEach((group) => {
    const firstPeriod = group[0];
    if (!firstPeriod?.groupId) return; // Skip if no valid group ID
    const groupId = firstPeriod.groupId;

    // Check all periods in this group against existing lanes
    let assignedLane = -1;

    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i];
      if (!lane) continue;

      const laneHasOverlap = group.some((period) =>
        lane.some((existingPeriod) => periodsOverlap(period, existingPeriod))
      );

      if (!laneHasOverlap) {
        assignedLane = i;
        break;
      }
    }

    // If no available lane found, create new lane
    if (assignedLane === -1) {
      assignedLane = lanes.length;
      lanes.push([]);
    }

    // Assign all periods in group to this lane
    const targetLane = lanes[assignedLane];
    if (targetLane) {
      targetLane.push(...group);
    }
    laneAssignments.set(groupId, assignedLane);
  });

  // Assign ungrouped periods
  ungrouped.forEach((period) => {
    let assignedLane = -1;

    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i];
      if (!lane) continue;

      const laneHasOverlap = lane.some((existingPeriod) =>
        periodsOverlap(period, existingPeriod)
      );

      if (!laneHasOverlap) {
        assignedLane = i;
        break;
      }
    }

    if (assignedLane === -1) {
      assignedLane = lanes.length;
      lanes.push([]);
    }

    const targetLane = lanes[assignedLane];
    if (targetLane) {
      targetLane.push(period);
    }
  });

  // Return periods with assigned lane numbers
  return periods.map((period) => {
    const lane = period.groupId
      ? laneAssignments.get(period.groupId)!
      : lanes.findIndex((lane) => lane.includes(period));

    return { ...period, lane };
  });
};

/**
 * Add days to a date
 */
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Create 89-day countdown bar segments (green, orange, red)
 * @param targetDate The date the countdown ends at
 * @param truckId The truck ID for unique period IDs
 * @param barType The bar type identifier (allows indexed variations)
 */
const createCountdownBar = (
  targetDate: Date,
  truckId: string,
  barType: string
): TimelinePeriod[] => {
  const periods: TimelinePeriod[] = [];
  const groupId = `${truckId}-${barType}`;

  // Calculate countdown start (89 days before target)
  const countdownStart = addDays(targetDate, -89);

  // Create shared boundary dates to ensure no gaps
  const day60 = addDays(countdownStart, 60);
  const day80 = addDays(countdownStart, 80);

  // Segment 1: Days 1-60 (GREEN) - LEFT rounded corners only
  const greenStart = countdownStart;
  const greenEnd = day60;
  periods.push({
    id: `${truckId}-${barType}-green`,
    type: 'countdown-green',
    startDate: greenStart,
    endDate: greenEnd,
    color: '#34d399', // Brighter Green (was #10b981)
    label: `${barType} - Safe (60 days)`,
    targetDate,
    daysRemaining: 60,
    groupId,
    segmentPosition: 'start',
  });

  // Segment 2: Days 61-80 (ORANGE) - NO rounded corners
  const orangeStart = day60;
  const orangeEnd = day80;
  periods.push({
    id: `${truckId}-${barType}-orange`,
    type: 'countdown-orange',
    startDate: orangeStart,
    endDate: orangeEnd,
    color: '#fbbf24', // Brighter Orange (was #f59e0b)
    label: `${barType} - Warning (20 days)`,
    targetDate,
    daysRemaining: 20,
    groupId,
    segmentPosition: 'middle',
  });

  // Segment 3: Days 81-89 (RED) - RIGHT rounded corners only
  const redStart = day80;
  const redEnd = targetDate;
  periods.push({
    id: `${truckId}-${barType}-red`,
    type: 'countdown-red',
    startDate: redStart,
    endDate: redEnd,
    color: '#f87171', // Brighter Red (was #ef4444)
    label: `${barType} - Critical (9 days)`,
    targetDate,
    daysRemaining: 9,
    groupId,
    segmentPosition: 'end',
  });

  return periods;
};

/**
 * Extract month and day from a date, ignoring year
 */
const getMonthAndDay = (date: Date): { month: number; day: number } => {
  return {
    month: date.getMonth(), // 0-11
    day: date.getDate(), // 1-31
  };
};

/**
 * Generate all occurrences of a recurring date within a date range
 * @param monthDay The month (0-11) and day (1-31) of the recurring date
 * @param startYear The first year to consider
 * @param endYear The last year to consider
 * @returns Array of Date objects for each occurrence
 */
const generateRecurringDates = (
  monthDay: { month: number; day: number },
  startYear: number,
  endYear: number
): Date[] => {
  const dates: Date[] = [];

  for (let year = startYear; year <= endYear; year++) {
    const date = new Date(year, monthDay.month, monthDay.day);
    dates.push(date);
  }

  return dates;
};

/**
 * Calculate timeline periods for a truck (Recurring Bi-Annual System)
 * Generates 89-day countdown bars for EVERY occurrence of registration and bi-annual dates
 */
export const calculatePeriods = (truck: Truck): TimelinePeriod[] => {
  const periods: TimelinePeriod[] = [];
  const today = getTodayDate();

  // Bi-Annual System: Two recurring countdown bars per year
  if (truck.registrationDate && truck.biAnnualDate) {
    const registrationDate = parseDate(truck.registrationDate);
    const biAnnualDate = parseDate(truck.biAnnualDate);

    if (registrationDate && biAnnualDate) {
      // Extract month/day (ignore year - these are recurring dates)
      const registrationMonthDay = getMonthAndDay(registrationDate);
      const biAnnualMonthDay = getMonthAndDay(biAnnualDate);

      // Generate occurrences for a reasonable range (3 years back, 2 years forward)
      const currentYear = today.getFullYear();
      const startYear = currentYear - 3;
      const endYear = currentYear + 2;

      // Generate all registration date occurrences
      const registrationOccurrences = generateRecurringDates(registrationMonthDay, startYear, endYear);
      registrationOccurrences.forEach((occurrence) => {
        const countdownBars = createCountdownBar(occurrence, truck.id, 'registration');
        periods.push(...countdownBars);
      });

      // Generate all bi-annual date occurrences
      const biAnnualOccurrences = generateRecurringDates(biAnnualMonthDay, startYear, endYear);
      biAnnualOccurrences.forEach((occurrence) => {
        const countdownBars = createCountdownBar(occurrence, truck.id, 'bi-annual');
        periods.push(...countdownBars);
      });
    }
  }

  return periods;
};

/**
 * Generate timeline data for all trucks
 */
export const generateTimelineData = (trucks: Truck[]): TruckTimelineData[] => {
  return trucks.map(truck => ({
    truckId: truck.id,
    truckName: truck.name,
    vin: truck.vin || 'N/A',
    licensePlate: truck.licensePlate || 'N/A',
    periods: calculatePeriods(truck),
  }));
};

/**
 * Check if date is within viewport
 */
export const isDateInViewport = (date: Date, viewport: TimelineViewport): boolean => {
  return date >= viewport.startDate && date <= viewport.endDate;
};

/**
 * Clamp date to viewport boundaries
 */
export const clampToViewport = (date: Date, viewport: TimelineViewport): Date => {
  if (date < viewport.startDate) return viewport.startDate;
  if (date > viewport.endDate) return viewport.endDate;
  return date;
};
